import React, {
	type ReactElement,
	useCallback,
	useEffect,
	useState,
	type ReactNode,
	useRef,
} from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import type { AnalyticsAttributes } from '../../../../common/types/analytics/index.tsx';
import type { Command } from '../../../../common/types/commands/index.tsx';
import { useCommandPaletteExpandedCommand } from '../../../../controllers/command-palette/index.tsx';

export const CommandBodyErrorBoundary = ({
	teamName,
	attributes,
	fallback,
	children,
}: {
	teamName?: string;
	attributes?: AnalyticsAttributes;
	fallback?: (props: { error: Error }) => ReactElement;
	children: ReactNode;
	expandedCommand?: Command;
}): React.JSX.Element => {
	const [{ expandedCommand }] = useCommandPaletteExpandedCommand();

	const capturedErrorRef = useRef<Error | undefined>();

	const [prevExpandedCommand, setPrevExpandedCommand] = useState(expandedCommand);
	const [hasReset, setHasReset] = useState(false);
	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
		if (hasReset) {
			capturedErrorRef.current = undefined;

			// eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
			setHasReset(false);
		}
	}, [hasReset]);

	const onRetry = useCallback(() => {
		setHasReset(true);
	}, []);

	const getCapturedError = useCallback(() => {
		return capturedErrorRef.current;
	}, [capturedErrorRef]);

	// This is so that changing body triggers a reset if an error was caught and then the user goes back a level or to another command list
	// Without this, the body would not rerender, and the fallback would remain present
	useEffect(() => {
		if (getCapturedError() && !hasReset && prevExpandedCommand !== expandedCommand) {
			onRetry();
		}
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
		setPrevExpandedCommand(expandedCommand);
	}, [expandedCommand, hasReset, onRetry, prevExpandedCommand, getCapturedError]);

	return (
		<JSErrorBoundary
			id="command.body"
			packageName="jiraCommandPalette"
			teamName={teamName || 'deliveroo'}
			attributes={attributes}
			onError={(_location, error) => {
				capturedErrorRef.current = error;
			}}
			fallback={fallback || 'unmount'}
			resetCaughtError={hasReset ? getCapturedError() : undefined}
		>
			{children}
		</JSErrorBoundary>
	);
};
